/**
 * SessionOverview — shared component that renders a session-based production
 * progress view with sub-session grids, progress bars, and operator/packer status.
 *
 * Accepts `sessions` and `sessionsLoading` as props — the parent is responsible
 * for fetching production sessions (it already does so for KPI cards).
 *
 * Used by Manager Dashboard (behind the Station/Session toggle).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Scissors, PackageCheck } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { formatYield } from '../hooks/useYieldFactor';
import { formatShortDate } from '../utils/dateUtils';
import { getStatusBadgeClass } from '../utils/statusColors';

interface SessionOverviewProps {
  sessions: any[];
  sessionsLoading: boolean;
}

const SessionOverview: React.FC<SessionOverviewProps> = ({ sessions, sessionsLoading }) => {
  const { t } = useLanguage();

  // Filter sessions: today + still-active from previous days, exclude cancelled
  const todaySessions = React.useMemo(() => {
    const todayStr = new Date().toLocaleDateString('en-CA');
    return sessions.filter((s: any) => {
      if (s.status === 'cancelled') return false;
      const scheduledDate = s.scheduled_date?.split('T')[0];
      const isToday = scheduledDate === todayStr;
      const isPastAndStillRunning = scheduledDate < todayStr && ['active', 'paused'].includes(s.status);
      return isToday || isPastAndStillRunning;
    });
  }, [sessions]);

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-2 xl:mb-3">
        <h2 className="text-base xl:text-lg font-semibold text-gray-900 dark:text-white">
          {t('manager.productionProgressOverview')}
        </h2>
        <Link
          to="/production-sessions"
          className="text-sm xl:text-base text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          {t('common.viewAll')}
        </Link>
      </div>

      {/* Body */}
      {sessionsLoading ? (
        <div className="flex items-center justify-center py-4 flex-1">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
        </div>
      ) : todaySessions.length === 0 ? (
        <p className="text-sm xl:text-base text-gray-400 dark:text-gray-500 text-center py-4 italic flex-1 flex items-center justify-center">
          {t('manager.noSessionsToday')}
        </p>
      ) : (
        <div className={todaySessions.length >= 3 ? "grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2 xl:gap-3 flex-1" : todaySessions.length === 2 ? "grid grid-cols-1 md:grid-cols-2 gap-2 xl:gap-3 flex-1" : "space-y-2 xl:space-y-3 flex-1"}>
          {todaySessions.map((session: any) => {
            // Filter out cancelled sub-sessions
            const subSessions = (session.sub_sessions || []).filter(
              (sub: any) => sub.status !== 'cancelled'
            );

            // Calculate overall progress across all non-cancelled sub-sessions
            const totalBoxesProduced = subSessions.reduce(
              (sum: number, sub: any) => sum + (Number(sub.boxes_produced) || 0), 0
            );
            const totalExpectedBoxes = subSessions.reduce(
              (sum: number, sub: any) => sum + (Number(sub.expected_boxes) || 0), 0
            );
            const totalActualWeight = subSessions.reduce(
              (sum: number, sub: any) => sum + (Number(sub.actual_output_weight) || 0), 0
            );
            const totalTargetWeight = subSessions.reduce(
              (sum: number, sub: any) => sum + (Number(sub.target_output_weight) || 0), 0
            );

            // Prefer box-based progress, fallback to weight-based
            const useBoxProgress = totalExpectedBoxes > 0;
            const overallPercent = useBoxProgress
              ? Math.min((totalBoxesProduced / totalExpectedBoxes) * 100, 100)
              : totalTargetWeight > 0
              ? Math.min((totalActualWeight / totalTargetWeight) * 100, 100)
              : 0;

            const overallLabel = useBoxProgress
              ? `${totalBoxesProduced} / ${totalExpectedBoxes} ${t('manager.boxes')}`
              : totalTargetWeight > 0
              ? `${totalActualWeight.toFixed(2)} / ${totalTargetWeight.toFixed(2)} ${t('manager.lbs')}`
              : '';

            // Derive effective status from sub-sessions (least advanced)
            const statusPriority: Record<string, number> = { pending: 0, submitted: 1, received: 2, ready: 3, paused: 4, active: 5, completed: 6 };
            const effectiveStatus = subSessions.length > 0
              ? subSessions.reduce((least: string, sub: any) =>
                  (statusPriority[sub.status] ?? 99) < (statusPriority[least] ?? 99) ? sub.status : least
                , subSessions[0].status)
              : session.status;

            const statusBadgeClass = getStatusBadgeClass(effectiveStatus);

            const overallBarColor = ['active', 'in_progress'].includes(effectiveStatus)
              ? 'bg-green-500'
              : effectiveStatus === 'paused'
              ? 'bg-amber-500'
              : effectiveStatus === 'completed'
              ? 'bg-green-500'
              : 'bg-blue-500';

            return (
              <div
                key={session.session_number}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 xl:p-4"
              >
                {/* Line 1: Session number + planned date + status */}
                <div className="flex items-center justify-between mb-1 xl:mb-2">
                  <span className="text-xs xl:text-sm text-gray-400 dark:text-gray-500">
                    {session.session_number}
                    {session.scheduled_date && (
                      <span className="ml-2 text-gray-300 dark:text-gray-600">
                        {formatShortDate(session.scheduled_date)}
                      </span>
                    )}
                  </span>
                  <span className={`px-1.5 xl:px-2 py-0.5 text-xs xl:text-sm font-medium rounded-full ${statusBadgeClass}`}>
                    {t(`status.${effectiveStatus}`) || effectiveStatus}
                  </span>
                </div>
                {/* Line 2: Finished product info */}
                <div className="flex items-center gap-2 flex-wrap mb-1 xl:mb-2">
                  <span className="text-xs xl:text-sm font-medium text-indigo-600 dark:text-indigo-400 shrink-0">
                    {session.product_sku}
                  </span>
                  <h3 className="text-base xl:text-lg font-semibold text-gray-900 dark:text-white">
                    {session.product_name}
                  </h3>
                  {session.product_chinese_name && (
                    <span className="text-sm xl:text-base text-gray-500 dark:text-gray-400">
                      {session.product_chinese_name}
                    </span>
                  )}
                  {session.packing && (
                    <span className="text-xs xl:text-sm px-1.5 py-px rounded bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 shrink-0">
                      {session.packing}
                    </span>
                  )}
                </div>

                {/* Raw material info */}
                {session.raw_material_item_number && (
                  <div className="flex items-center gap-2 flex-wrap text-xs xl:text-sm text-gray-500 dark:text-gray-400 mb-1 xl:mb-2">
                    <span className="font-medium text-orange-600 dark:text-orange-400">{session.raw_material_item_number}</span>
                    <span>{session.raw_material_name}</span>
                    {session.raw_material_chinese_name && (
                      <span className="text-gray-400 dark:text-gray-500">{session.raw_material_chinese_name}</span>
                    )}
                    {Number(session.raw_material_yield_factor) > 0 && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">|</span>
                        <span>{t('supervisor.yield')}: {formatYield(Number(session.raw_material_yield_factor))}</span>
                      </>
                    )}
                    {Number(session.raw_material_unit_weight) > 0 && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">|</span>
                        <span>{t('supervisor.caseWeight')}: {Number(session.raw_material_unit_weight).toFixed(2)} {t('manager.lbs')}</span>
                      </>
                    )}
                    {Number(session.raw_material_cases) > 0 && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">|</span>
                        <span>{t('supervisor.estCases')}: {session.raw_material_cases}</span>
                      </>
                    )}
                    {Number(session.raw_material_cases) > 0 && Number(session.raw_material_unit_weight) > 0 && (
                      <>
                        <span className="text-gray-300 dark:text-gray-600">|</span>
                        <span className="font-medium">{t('operator.rawMaterial')}: {(Number(session.raw_material_cases) * Number(session.raw_material_unit_weight)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {t('manager.lbs')}</span>
                      </>
                    )}
                  </div>
                )}

                {/* Overall progress bar */}
                {overallLabel && (
                  <div className="mb-2 xl:mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs xl:text-sm text-gray-500 dark:text-gray-400 shrink-0">{t('manager.overall')}</span>
                      <div className="flex-1 h-1.5 xl:h-2 rounded-full bg-gray-200 dark:bg-gray-700">
                        <div
                          className={`h-1.5 xl:h-2 rounded-full ${overallBarColor} transition-all duration-300`}
                          style={{ width: `${overallPercent}%` }}
                        />
                      </div>
                      <span className="text-sm xl:text-base font-semibold text-gray-600 dark:text-gray-300 shrink-0">
                        {overallPercent.toFixed(2)}% · {overallLabel}
                      </span>
                    </div>
                  </div>
                )}

                {/* Sub-session grid */}
                {subSessions.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 xl:gap-3">
                    {subSessions.map((sub: any) => {
                      const boxesProduced = Number(sub.boxes_produced) || 0;
                      const expectedBoxes = Number(sub.expected_boxes) || 0;
                      const actualWeight = Number(sub.actual_output_weight) || 0;
                      const targetWeight = Number(sub.target_output_weight) || 0;

                      const useBoxes = expectedBoxes > 0;
                      const subPercent = useBoxes
                        ? Math.min((boxesProduced / expectedBoxes) * 100, 100)
                        : targetWeight > 0
                        ? Math.min((actualWeight / targetWeight) * 100, 100)
                        : 0;

                      const subLabel = useBoxes
                        ? `${boxesProduced}/${expectedBoxes}`
                        : targetWeight > 0
                        ? `${actualWeight.toFixed(2)}/${targetWeight.toFixed(2)}`
                        : '';

                      const subBadgeClass = getStatusBadgeClass(sub.status);

                      const subBarColor = ['active', 'in_progress'].includes(sub.status)
                        ? 'bg-green-500'
                        : sub.status === 'paused'
                        ? 'bg-amber-500'
                        : sub.status === 'completed'
                        ? 'bg-green-500'
                        : 'bg-blue-500';

                      return (
                        <div
                          key={sub.sub_session_number}
                          className="border border-gray-100 dark:border-gray-600 rounded p-2 xl:p-3 bg-gray-50 dark:bg-gray-700/50"
                        >
                          <div className="flex items-center justify-between mb-1 xl:mb-1.5">
                            <div className="flex items-center gap-2">
                              <span className="text-sm xl:text-base font-medium text-gray-900 dark:text-white">
                                {sub.station_name || '—'}
                              </span>
                              {(sub.started_at || sub.completed_at) && (
                                <span className="text-[10px] xl:text-xs text-gray-400 dark:text-gray-500">
                                  {sub.started_at && new Date(sub.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  {sub.started_at && sub.completed_at && ' – '}
                                  {sub.completed_at && new Date(sub.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </div>
                            <span className={`px-1 xl:px-1.5 py-px text-[10px] xl:text-xs font-medium rounded-full ${subBadgeClass}`}>
                              {t(`status.${sub.status}`) || sub.status}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs xl:text-sm text-gray-500 dark:text-gray-400 mb-1.5 xl:mb-2">
                            <span className={`inline-flex items-center gap-1 whitespace-nowrap basis-[calc(50%-0.25rem)] ${sub.operator_status === 'on_break' ? 'bg-amber-100 dark:bg-amber-900/30 rounded-full px-2 py-0.5 animate-pulse' : sub.operator_status === 'scanned_out' ? 'bg-red-100 dark:bg-red-900/30 rounded-full px-2 py-0.5' : ''}`}>
                              <Scissors className="w-3 h-3 text-green-500 dark:text-green-400 shrink-0" />
                              {sub.operator_name || '—'}
                            </span>
                            <span className={`inline-flex items-center gap-1 whitespace-nowrap basis-[calc(50%-0.25rem)] ${sub.packer_status === 'on_break' ? 'bg-amber-100 dark:bg-amber-900/30 rounded-full px-2 py-0.5 animate-pulse' : sub.packer_status === 'scanned_out' ? 'bg-red-100 dark:bg-red-900/30 rounded-full px-2 py-0.5' : ''}`}>
                              <PackageCheck className="w-3 h-3 text-blue-500 dark:text-blue-400 shrink-0" />
                              {sub.packer_name || '—'}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="flex-1 h-1 xl:h-1.5 rounded-full bg-gray-200 dark:bg-gray-700">
                              <div
                                className={`h-1 xl:h-1.5 rounded-full ${subBarColor} transition-all duration-300`}
                                style={{ width: `${subPercent}%` }}
                              />
                            </div>
                            <span className="text-xs xl:text-sm font-semibold text-gray-500 dark:text-gray-400 shrink-0">
                              {subPercent.toFixed(2)}% · {subLabel}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default SessionOverview;
